import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useUpdateBinServiceType } from '@/hooks';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';

export interface EditServiceTypeModalProps {
  open: boolean;
  assetName: string;
  totalCount: number;
  initialServiceableCount: number;
  initialNonServiceableCount: number;
  /** Ordered bin IDs: serviceable bins first, then non-serviceable */
  serviceableBinIds: string[];
  nonServiceableBinIds: string[];
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditServiceTypeModal: React.FC<EditServiceTypeModalProps> = ({
  open,
  assetName,
  totalCount,
  initialServiceableCount,
  initialNonServiceableCount,
  serviceableBinIds,
  nonServiceableBinIds,
  onClose,
  onSuccess,
}) => {
  const [serviceableCount, setServiceableCount] = useState(initialServiceableCount);
  const [nonServiceableCount, setNonServiceableCount] = useState(initialNonServiceableCount);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate, isPending, reset } = useUpdateBinServiceType();

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setServiceableCount(initialServiceableCount);
      setNonServiceableCount(initialNonServiceableCount);
      setErrorMessage(null);
      reset();
    }
  }, [open, initialServiceableCount, initialNonServiceableCount, reset]);

  const handleServiceableChange = useCallback(
    (newValue: number) => {
      const clamped = Math.max(0, Math.min(totalCount, newValue));
      setServiceableCount(clamped);
      setNonServiceableCount(totalCount - clamped);
      setErrorMessage(null);
    },
    [totalCount],
  );

  const handleSliderChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      const value = Array.isArray(newValue) ? (newValue[0] ?? 0) : newValue;
      handleServiceableChange(value);
    },
    [handleServiceableChange],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSave = useCallback(() => {
    if (serviceableCount + nonServiceableCount !== totalCount) {
      setErrorMessage(
        `Counts must sum to ${totalCount}. Current sum: ${serviceableCount + nonServiceableCount}`,
      );
      return;
    }

    // Build per-bin update list.
    // Serviceable bins first: keep first `serviceableCount` as Serviceable, flip the rest.
    // Non-serviceable bins: flip first bins to Serviceable if needed, keep rest as NonServiceable.
    const updates: Array<{ binId: string; serviceType: 'Serviceable' | 'NonServiceable' }> = [];

    // How many currently-serviceable bins need to flip to non-serviceable
    const svcToFlip = Math.max(0, serviceableBinIds.length - serviceableCount);
    // How many currently-non-serviceable bins need to flip to serviceable
    const nsvcToFlip = Math.max(0, serviceableCount - serviceableBinIds.length);

    serviceableBinIds.forEach((binId, index) => {
      if (index >= serviceableBinIds.length - svcToFlip) {
        updates.push({ binId, serviceType: 'NonServiceable' });
      }
    });

    nonServiceableBinIds.forEach((binId, index) => {
      if (index < nsvcToFlip) {
        updates.push({ binId, serviceType: 'Serviceable' });
      }
    });

    if (updates.length === 0) {
      // No changes needed
      handleClose();
      return;
    }

    setErrorMessage(null);

    mutate(
      { updates },
      {
        onSuccess: () => {
          handleClose();
          onSuccess?.();
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  }, [
    serviceableCount,
    nonServiceableCount,
    totalCount,
    serviceableBinIds,
    nonServiceableBinIds,
    mutate,
    handleClose,
    onSuccess,
  ]);

  const hasChanges =
    serviceableCount !== initialServiceableCount ||
    nonServiceableCount !== initialNonServiceableCount;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Service Type</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {assetName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total items: {totalCount}
            </Typography>
          </Box>

          {/* Count labels */}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle1">
              Non-Serviceable: <strong>{nonServiceableCount}</strong>
            </Typography>
            <Typography variant="subtitle1">
              Serviceable: <strong>{serviceableCount}</strong>
            </Typography>
          </Stack>

          {/* Slider with +/- buttons */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => handleServiceableChange(serviceableCount - 1)}
                disabled={serviceableCount <= 0 || isPending}
                aria-label="Decrease serviceable count"
              >
                <RemoveIcon />
              </IconButton>
              <Slider
                value={serviceableCount}
                onChange={handleSliderChange}
                min={0}
                max={totalCount}
                step={1}
                disabled={isPending || totalCount === 0}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `S: ${value}`}
                sx={{
                  flex: 1,
                  '& .MuiSlider-track': {
                    bgcolor: 'success.main',
                  },
                  '& .MuiSlider-rail': {
                    bgcolor: 'warning.light',
                  },
                  '& .MuiSlider-thumb': {
                    bgcolor: 'primary.main',
                  },
                }}
                aria-label="Service type distribution slider"
              />
              <IconButton
                onClick={() => handleServiceableChange(serviceableCount + 1)}
                disabled={serviceableCount >= totalCount || isPending}
                aria-label="Increase serviceable count"
              >
                <AddIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ px: 6, mt: -0.5 }}>
              <Typography variant="caption" color="warning.main">
                NS
              </Typography>
              <Typography variant="caption" color="success.main">
                S
              </Typography>
            </Stack>
          </Box>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isPending}>
          Cancel
        </Button>
        <ViewerGuardedButton onClick={handleSave} variant="contained" disabled={isPending || !hasChanges}>
          {isPending ? 'Saving...' : 'Save'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
